import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoginPage from './LoginPage';
import OTPVerificationPage from './OTPVerificationPage';
import { environment, routes } from '../config/environment';
import voteBoosterService from '../services/voteBoosterService';
import { getApiHeaders, getApiHeadersForGet } from '../utils/apiUtils';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Divider,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  ArrowBack as ArrowBackIcon,
  Star as StarIcon,
  Close as CloseIcon
} from '@mui/icons-material';

interface Participant {
  id: string;
  contest_id: string;
  participant_name: string;
  participant_id: string | null;
  votes: number;
  photo_url: string | null;
  petflix_post_id: string | null;
  created_at: string;
  updated_at: string;
}

interface ContestInfo {
  id: string;
  name: string;
  contest_state: string;
  isFinished: boolean;
  isCancelled: boolean;
  isActive: boolean;
  vote_type?: string;
}

interface ContestGroup {
  contest_id: string;
  contest_name?: string;
  contest_state?: string;
  isFinished?: boolean;
  isCancelled?: boolean;
  isActive?: boolean;
  vote_type?: string;
  participants: Participant[];
}

const ContestVotePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedContestId = searchParams.get('contestId') || '';
  const [expandedContestId, setExpandedContestId] = useState<string>('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');

  const [participants, setParticipants] = useState<Participant[]>([]);
  const [contestGroups, setContestGroups] = useState<ContestGroup[]>([]);
  const [contestInfoMap, setContestInfoMap] = useState<Record<string, ContestInfo>>({});
  const [localVotes, setLocalVotes] = useState<Record<string, number>>({});
  const [votedParticipantIds, setVotedParticipantIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  
  // Estados para compra de votos
  const [showVoteBoosterModal, setShowVoteBoosterModal] = useState(false);
  const [selectedParticipantForBooster, setSelectedParticipantForBooster] = useState<Participant | null>(null);
  const [buyingVotes, setBuyingVotes] = useState(false);

  // Restaurar sesión persistente al cargar
  useEffect(() => {
    try {
      const persisted = localStorage.getItem('pc_isLoggedIn');
      const savedEmail = localStorage.getItem('pc_email') || '';
      if (persisted === 'true') {
        setIsLoggedIn(true);
        setUserEmail(savedEmail);
        setShowOTP(false);
      }
    } catch (e) {
      // ignorar errores de storage
    }
  }, []);

  // Cargar participantes y votos del usuario cuando el usuario esté logueado
  useEffect(() => {
    if (isLoggedIn && userEmail) {
      loadParticipants();
      loadUserVotes();
    }
  }, [isLoggedIn, userEmail]);

  // Auto-scroll al concurso preseleccionado desde query param
  useEffect(() => {
    if (preselectedContestId && contestGroups.length > 0) {
      setExpandedContestId(preselectedContestId);
      const timer = setTimeout(() => {
        const el = document.getElementById(`vote-contest-${preselectedContestId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [preselectedContestId, contestGroups.length]);

  // Cargar información de estado de los concursos
  const loadContestStates = async (contestIds: string[]) => {
    const uniqueContestIds = [...new Set(contestIds)];
    const statePromises = uniqueContestIds.map(async (contestId) => {
      try {
        const response = await fetch(`${environment.apiBaseUrl}/contests/${contestId}/state`, {
          headers: getApiHeadersForGet()
        });
        if (response.ok) {
          const data = await response.json();
          return { contestId, info: data.contest };
        }
      } catch (error) {
        console.error(`Error loading state for contest ${contestId}:`, error);
      }
      return null;
    });

    const results = await Promise.all(statePromises);
    const infoMap: Record<string, ContestInfo> = {};
    results.forEach(result => {
      if (result) {
        infoMap[result.contestId] = result.info;
      }
    });
    setContestInfoMap(infoMap);
  };

  // Cargar estados cuando cambian los participantes
  useEffect(() => {
    if (participants.length === 0) return;

    const contestIds = [...new Set(participants.map(p => p.contest_id))];
    if (contestIds.length > 0) {
      loadContestStates(contestIds);
    }
  }, [participants]);

  // Agrupar participantes por contest_id cuando cambian participantes o contestInfoMap
  useEffect(() => {
    const grouped = participants.reduce((acc, participant) => {
      const contestId = participant.contest_id;
      if (!acc[contestId]) {
        acc[contestId] = [];
      }
      acc[contestId].push(participant);
      return acc;
    }, {} as Record<string, Participant[]>);

    const groups: ContestGroup[] = Object.entries(grouped).map(([contest_id, participants]) => {
      const info = contestInfoMap[contest_id];
      return {
        contest_id,
        contest_name: info?.name,
        contest_state: info?.contest_state,
        isFinished: info?.isFinished,
        isCancelled: info?.isCancelled,
        isActive: info?.isActive,
        vote_type: info?.vote_type,
        participants: participants.sort((a, b) => b.votes - a.votes)
      };
    });

    setContestGroups(groups);

    // Inicializar localVotes con los valores actuales
    const initialVotes: Record<string, number> = {};
    participants.forEach(p => {
      initialVotes[p.id] = p.votes;
    });
    setLocalVotes(initialVotes);
  }, [participants, contestInfoMap]);

  const loadParticipants = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${environment.apiBaseUrl}/test`, {
        headers: getApiHeadersForGet()
      });
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setParticipants(data.data || []);

      // Inicializar localVotes
      const initialVotes: Record<string, number> = {};
      (data.data || []).forEach((p: Participant) => {
        initialVotes[p.id] = p.votes;
      });
      setLocalVotes(initialVotes);
    } catch (err: any) {
      console.error('Error loading participants:', err);
      setError(err.message || 'Error al cargar participantes');
    } finally {
      setLoading(false);
    }
  };

  const loadUserVotes = async () => {
    if (!userEmail) return;

    try {
      const response = await fetch(`${environment.apiBaseUrl}/test/user-votes?email=${encodeURIComponent(userEmail)}`, {
        headers: getApiHeadersForGet()
      });
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setVotedParticipantIds(new Set(data.votedParticipantIds || []));
    } catch (err: any) {
      console.error('Error loading user votes:', err);
      // No mostrar error al usuario, solo log
    }
  };

  const handleVoteChange = (participantId: string, delta: number) => {
    // Verificar si el usuario ya votó por este participante
    const hasVoted = votedParticipantIds.has(participantId);
    const current = localVotes[participantId] || 0;
    const participant = participants.find(p => p.id === participantId);
    const original = participant?.votes || 0;

    // Si el usuario ya votó HOY, NO puede incrementar en absoluto
    // Solo puede decrementar o mantener el valor actual
    if (hasVoted && delta > 0) {
      setSaveMessage({
        type: 'error',
        text: 'Ya has votado por este participante hoy. Solo puedes votar una vez por día por participante.'
      });
      return;
    }

    // Si no ha votado y está incrementando, permitir solo un incremento de 1
    // Esto asegura que solo puede votar una vez (incrementar de X a X+1)
    if (!hasVoted && delta > 0) {
      // Verificar que no esté intentando incrementar más de 1 voto
      const newValue = current + delta;
      if (newValue > original + 1) {
        setSaveMessage({
          type: 'error',
          text: 'Solo puedes agregar un voto a la vez. Guarda los cambios antes de agregar más votos.'
        });
        return;
      }
    }

    setLocalVotes(prev => {
      const currentVotes = prev[participantId] || 0;
      const newValue = Math.max(0, currentVotes + delta); // No permitir valores negativos
      return {
        ...prev,
        [participantId]: newValue
      };
    });
    setSaveMessage(null);
  };

  const handleSaveVotes = async () => {
    setSaving(true);
    setSaveMessage(null);

    try {
      // Encontrar participantes con cambios y validar
      const changes: Array<{ id: string; votes: number }> = [];
      let validationError: string | null = null;

      participants.forEach(participant => {
        const originalVotes = participant.votes;
        const newVotes = localVotes[participant.id] ?? originalVotes;
        const hasVoted = votedParticipantIds.has(participant.id);

        // Validar que si ya votó HOY, no esté intentando incrementar
        if (hasVoted && newVotes > originalVotes) {
          validationError = `No puedes incrementar los votos de "${participant.participant_name}" porque ya votaste por este participante hoy.`;
          return;
        }

        if (originalVotes !== newVotes) {
          changes.push({ id: participant.id, votes: newVotes });
        }
      });

      // Si hubo error de validación, mostrar mensaje y salir
      if (validationError) {
        setSaveMessage({
          type: 'error',
          text: validationError
        });
        setSaving(false);
        return;
      }

      if (changes.length === 0) {
        setSaveMessage({ type: 'info', text: 'No hay cambios para guardar' });
        setSaving(false);
        return;
      }

      // Actualizar todos los participantes con cambios
      const updatePromises = changes.map(change =>
        fetch(`${environment.apiBaseUrl}/test/${change.id}/votes`, {
          method: 'PUT',
          headers: getApiHeaders(),
          body: JSON.stringify({
            votes: change.votes,
            userEmail: userEmail
          }),
        })
      );

      const results = await Promise.all(updatePromises);

      // Verificar que todas las actualizaciones fueron exitosas
      const failed = results.filter(r => !r.ok);
      if (failed.length > 0) {
        throw new Error(`${failed.length} actualizaciones fallaron`);
      }

      // Recargar participantes y votos del usuario para obtener los valores actualizados
      await loadParticipants();
      await loadUserVotes();

      setSaveMessage({
        type: 'success',
        text: `Se guardaron ${changes.length} cambio(s) exitosamente`
      });
    } catch (err: any) {
      console.error('Error saving votes:', err);
      setSaveMessage({
        type: 'error',
        text: err.message || 'Error al guardar los votos'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogin = (email: string) => {
    setUserEmail(email);
    setShowOTP(true);
    setError('');
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    console.error('Login error:', errorMessage);
  };

  const handleOTPVerify = (otp: string) => {
    console.log('OTP Verified:', otp);
    setIsLoggedIn(true);
    setShowOTP(false);
    try {
      localStorage.setItem('pc_isLoggedIn', 'true');
      if (userEmail) localStorage.setItem('pc_email', userEmail);
    } catch (e) {
      // ignorar errores de storage
    }
  };

  const handleResendOTP = () => {
    console.log('Resending OTP to:', userEmail);
  };

  const handleChangeEmail = () => {
    setShowOTP(false);
    setUserEmail('');
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setShowOTP(false);
    setUserEmail('');
    setError('');
    try {
      localStorage.removeItem('pc_isLoggedIn');
      localStorage.removeItem('pc_email');
    } catch (e) {
      // ignorar errores de storage
    }
  };

  const hasChanges = () => {
    return participants.some(participant => {
      const originalVotes = participant.votes;
      const newVotes = localVotes[participant.id] ?? originalVotes;
      return originalVotes !== newVotes;
    });
  };

  // Función para abrir modal de compra de votos
  const handleOpenVoteBoosterModal = (participant: Participant) => {
    setSelectedParticipantForBooster(participant);
    setShowVoteBoosterModal(true);
  };

  // Función para comprar vote booster
  const handleBuyVoteBooster = async (tier: string) => {
    if (!selectedParticipantForBooster || !userEmail) return;
    
    setBuyingVotes(true);
    try {
      const tierInfo = voteBoosterService.getTierInfo(tier);
      if (!tierInfo) {
        throw new Error('Tier inválido');
      }

      // Preparar el body para el endpoint de vote-boosters
      const requestBody = {
        contest_id: selectedParticipantForBooster.contest_id,
        participant_id: selectedParticipantForBooster.id,
        user_email: userEmail,  // El endpoint espera user_email, no userEmail
        booster_tier: tier
      };

      // Log para debug: mostrar qué se está enviando
      console.log('[FRONTEND] Enviando compra de votos a /api/vote-boosters:', JSON.stringify(requestBody, null, 2));
      console.log('[FRONTEND] booster_tier enviado:', tier, 'tipo:', typeof tier);

      // Usar el endpoint específico de vote-boosters en lugar de /api/test/:id/votes
      const response = await fetch(`${environment.apiBaseUrl}/vote-boosters`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al comprar votos');
      }

      const result = await response.json();
      
      // Recargar participantes para mostrar los votos actualizados
      await loadParticipants();
      
      setSaveMessage({
        type: 'success',
        text: `¡${result.votes_added || tierInfo.votes} votos agregados exitosamente! (Tier: ${tier})`
      });
      
      setShowVoteBoosterModal(false);
      setSelectedParticipantForBooster(null);
    } catch (error: any) {
      console.error('Error purchasing vote booster:', error);
      setSaveMessage({
        type: 'error',
        text: error.message || 'Error al comprar votos'
      });
    } finally {
      setBuyingVotes(false);
    }
  };

  if (!isLoggedIn && !showOTP) {
    return <LoginPage onLogin={handleLogin} onError={handleError} />;
  }

  if (showOTP) {
    return (
      <OTPVerificationPage
        email={userEmail}
        onVerify={handleOTPVerify}
        onResend={handleResendOTP}
        onChangeEmail={handleChangeEmail}
      />
    );
  }

  return (
    <div className="napc-page min-h-screen text-napc-ink">
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box>
            <Box className="napc-eyebrow">Voting room</Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Sistema de Votación
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vota por los participantes de los concursos
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(routes.contest)}
            >
              Volver
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              Salir
            </Button>
          </Stack>
        </Box>

        {/* Mensajes */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {saveMessage && (
          <Alert
            severity={saveMessage.type === 'success' ? 'success' : saveMessage.type === 'error' ? 'error' : 'info'}
            sx={{ mb: 2 }}
            onClose={() => setSaveMessage(null)}
          >
            {saveMessage.text}
          </Alert>
        )}

        {/* Botones de acción */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadParticipants}
            disabled={loading || saving}
          >
            Recargar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSaveVotes}
            disabled={loading || saving || !hasChanges()}
            sx={{ minWidth: 150 }}
          >
            {saving ? 'Guardando...' : 'Guardar Votos'}
          </Button>
        </Box>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Lista de concursos y participantes */}
        {!loading && contestGroups.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No hay participantes registrados
            </Typography>
          </Paper>
        )}

        {!loading && contestGroups.map((group) => (
          <Card key={group.contest_id} id={`vote-contest-${group.contest_id}`} sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight="bold">
                  Concurso: {group.contest_name || ''}
                  {!group.contest_name && (
                    <CircularProgress size={16} sx={{ ml: 1, verticalAlign: 'middle' }} />
                  )}
                </Typography>
                {group.contest_name && group.isFinished && (
                  <Chip
                    label="Terminado"
                    size="small"
                    color="success"
                    variant="filled"
                  />
                )}
                {group.contest_name && group.isCancelled && (
                  <Chip
                    label="Cancelado"
                    size="small"
                    color="error"
                    variant="filled"
                  />
                )}
                {group.contest_name && group.isActive && (
                  <Chip
                    label="Activo"
                    size="small"
                    color="primary"
                    variant="filled"
                  />
                )}
                {group.contest_name && group.vote_type && (
                  <Chip
                    label={group.vote_type === 'judges' ? 'Votación por Jueces' : 'Votación por Usuarios'}
                    size="small"
                    color={group.vote_type === 'judges' ? 'secondary' : 'default'}
                    variant="outlined"
                  />
                )}
                <Chip
                  label={`${group.participants.length} participante(s)`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {group.participants.map((participant) => {
                  const currentVotes = localVotes[participant.id] ?? participant.votes;
                  const originalVotes = participant.votes;
                  const hasChanged = currentVotes !== originalVotes;
                  const hasVoted = votedParticipantIds.has(participant.id);
                  const canIncrement = !hasVoted || currentVotes < originalVotes;
                  // El botón de restar solo aparece si el usuario ha añadido un voto (localmente o ya guardado)
                  const canDecrement = hasVoted || currentVotes > originalVotes;

                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={participant.id}>
                      <Paper
                        elevation={hasChanged ? 3 : 1}
                        sx={{
                          p: 2,
                          border: hasChanged ? '2px solid' : '1px solid',
                          borderColor: hasChanged ? 'primary.main' : 'divider',
                          backgroundColor: hasChanged ? 'action.hover' : 'background.paper',
                          transition: 'all 0.2s'
                        }}
                      >
                        <Box sx={{ mb: 2 }}>
                          {participant.photo_url && (
                            <Box
                              component="img"
                              src={participant.photo_url}
                              alt={participant.participant_name}
                              sx={{
                                width: '100%',
                                height: 200,
                                objectFit: 'cover',
                                borderRadius: 1,
                                mb: 1
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                          <Typography variant="subtitle1" fontWeight="bold" noWrap>
                            {participant.participant_name}
                          </Typography>
                          {hasVoted && (
                            <Chip
                              label="Ya votaste hoy"
                              size="small"
                              color="success"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                          {hasChanged && !hasVoted && (
                            <Chip
                              label="Modificado"
                              size="small"
                              color="primary"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>

                        {/* Botón para comprar votos */}
                        <Box sx={{ mb: 2 }}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            startIcon={<StarIcon />}
                            onClick={() => handleOpenVoteBoosterModal(participant)}
                            sx={{ width: '100%' }}
                          >
                            Comprar Votos
                          </Button>
                        </Box>

                        {/* Sistema de votación normal para todos los concursos */}
                        {/* Comentado: Sistema de votación especial para jueces (selector 0-10)
                        {group.vote_type === 'judges' ? (
                          <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id={`vote-select-${participant.id}`}>Valoracion</InputLabel>
                            <Select
                              labelId={`vote-select-${participant.id}`}
                              value={currentVotes.toString()}
                              label="Valoracion"
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                setLocalVotes(prev => ({
                                  ...prev,
                                  [participant.id]: value
                                }));
                                setSaveMessage(null);
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: hasChanged ? 'primary.light' : 'background.paper'
                                }
                              }}
                            >
                              {Array.from({ length: 11 }, (_, i) => i).map((value) => (
                                <MenuItem key={value} value={value}>
                                  {value}
                                </MenuItem>
                              ))}
                            </Select>
                            {hasChanged && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, display: 'block' }}
                              >
                                (antes: {participant.votes})
                              </Typography>
                            )}
                          </FormControl>
                        ) : (
                        */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {canDecrement && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleVoteChange(participant.id, -1)}
                              disabled={currentVotes <= originalVotes}
                            >
                              <RemoveIcon />
                            </IconButton>
                          )}

                          <TextField
                            type="number"
                            value={currentVotes}
                            onChange={(e) => {
                              const value = Math.max(0, parseInt(e.target.value) || 0);
                              const original = participant.votes;
                              const hasVotedForThis = votedParticipantIds.has(participant.id);

                              // Si ya votó HOY y está intentando incrementar, prevenir
                              if (hasVotedForThis && value > original) {
                                setSaveMessage({
                                  type: 'error',
                                  text: 'Ya has votado por este participante hoy. Solo puedes votar una vez por día por participante.'
                                });
                                return;
                              }

                              setLocalVotes(prev => ({
                                ...prev,
                                [participant.id]: value
                              }));
                              setSaveMessage(null);
                            }}
                            inputProps={{
                              min: 0,
                              style: { textAlign: 'center', fontWeight: 'bold' }
                            }}
                            sx={{
                              width: 80,
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: hasChanged ? 'primary.light' : 'background.paper'
                              }
                            }}
                          />

                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleVoteChange(participant.id, 1)}
                            disabled={!canIncrement}
                            title={hasVoted ? 'Ya has votado por este participante hoy' : ''}
                          >
                            <AddIcon />
                          </IconButton>

                          {hasChanged && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              (antes: {participant.votes})
                            </Typography>
                          )}
                        </Box>
                        {/* )} */}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        ))}

        {/* Modal para comprar Vote Boosters */}
        {showVoteBoosterModal && selectedParticipantForBooster && (
          <Dialog 
            open={showVoteBoosterModal} 
            onClose={() => {
              setShowVoteBoosterModal(false);
              setSelectedParticipantForBooster(null);
            }} 
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              fontWeight: 'bold',
              color: '#111827',
              borderBottom: '1px solid #e5e7eb',
              pb: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <StarIcon sx={{ color: '#f59e0b' }} />
                Comprar Vote Boosters
              </Box>
              <IconButton onClick={() => {
                setShowVoteBoosterModal(false);
                setSelectedParticipantForBooster(null);
              }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
                Participante: <strong>{selectedParticipantForBooster.participant_name}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#6b7280' }}>
                Selecciona un tier de vote booster para agregar votos a este participante:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {voteBoosterService.getAllTiers().map((tier: any) => (
                  <Card key={tier.tier} sx={{ border: '1px solid #e5e7eb', '&:hover': { borderColor: '#9333ea' } }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111827' }}>
                            {tier.tier} = {tier.votes} votos
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            ${tier.price.toFixed(2)}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          onClick={() => handleBuyVoteBooster(tier.tier)}
                          disabled={buyingVotes}
                          sx={{
                            backgroundColor: '#9333ea',
                            '&:hover': { backgroundColor: '#7e22ce' }
                          }}
                        >
                          {buyingVotes ? 'Comprando...' : 'Comprar'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Nota:</strong> Los votos se agregarán inmediatamente después de la compra. 
                  Puedes comprar votos de manera ilimitada, excepto para el tier $0 (voto gratis) que solo se puede usar una vez.
                </Typography>
              </Alert>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
              <Button 
                onClick={() => {
                  setShowVoteBoosterModal(false);
                  setSelectedParticipantForBooster(null);
                }} 
                variant="outlined"
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Container>
    </div>
  );
};

export default ContestVotePage;

